/**
 * Leituras da administracao de empresas: `GET /api/admin/companies` e
 * `GET /api/admin/companies/:companyId` (backend real desde 25/09/2026,
 * src/api/admin.ts), so para `platform_admin` -- o servidor confere o papel,
 * nao o navegador.
 *
 * Decisoes nao obvias:
 *  - 404 `empresa_nao_encontrada` no detalhe vira `null` (a pagina mostra o
 *    vazio): com a rota existindo, "nao encontrada" e uma afirmacao verdadeira
 *    sobre o banco. Qualquer outro erro continua em `ErrorPanel`.
 *  - O servidor manda a URL do webhook como CAMINHO (`/webhooks/kiwify/<id>`),
 *    porque so o navegador sabe o endereco publico pelo qual o painel foi
 *    aberto. Aqui ela ganha a origem, para o "copiar" entregar a URL inteira.
 *  - No simulado, o detalhe devolve `null` quando o id nao esta no catalogo,
 *    para a pagina mostrar o vazio em vez de inventar uma empresa.
 */

import { ApiError, fetchOrSimulate, rangeQuery, type Envelope } from '../../lib/api';
import type { PeriodRange } from '../../types';
import { simularDetalheEmpresa, simularVisaoEmpresas } from './mock';
import type { CompaniesOverview, CompanyDetail } from './types';

/** Metricas do periodo por empresa (e o cadastro de todas, em `companies`). */
export function fetchCompanies(range: PeriodRange, signal: AbortSignal): Promise<Envelope<CompaniesOverview>> {
  return fetchOrSimulate('/api/admin/companies', rangeQuery(range), signal, () => simularVisaoEmpresas(range));
}

function comOrigem(url: string): string {
  return url.startsWith('/') ? `${window.location.origin}${url}` : url;
}

/** Tudo de uma empresa na janela. */
export async function fetchCompanyDetail(companyId: string, range: PeriodRange, signal: AbortSignal): Promise<Envelope<CompanyDetail | null>> {
  try {
    const envelope = await fetchOrSimulate<CompanyDetail | null>(`/api/admin/companies/${encodeURIComponent(companyId)}`, rangeQuery(range), signal, () =>
      simularDetalheEmpresa(companyId, range),
    );
    const detalhe = envelope.data;
    if (detalhe === null || envelope.simulated) {
      return envelope;
    }
    return {
      simulated: false,
      data: {
        ...detalhe,
        integrations: {
          ...detalhe.integrations,
          webhooks: detalhe.integrations.webhooks.map((w) => ({ ...w, webhookUrl: comOrigem(w.webhookUrl) })),
        },
      },
    };
  } catch (error) {
    if (error instanceof ApiError && error.status === 404 && error.code === 'empresa_nao_encontrada') {
      return { data: null, simulated: false };
    }
    throw error;
  }
}
